import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Loader2, BadgeCheck, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const HERITAGE = ["Woodland Mississippian", "Modern Freedmen", "Both Lineages", "Researching / Unsure"];
const RESEARCH = ["Completed genealogical research", "Research in progress", "Not yet started"];
const ENROLLMENT = ["Not enrolled with any federal/state tribe", "Descendant of an enrolled member", "Formerly enrolled", "Other"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", heritage: "", research_status: "",
    enrollment_status: "", location: "", heard_about: "", message: "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setSel = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.heritage || !form.research_status || !form.enrollment_status) {
      toast.error("Please complete the required fields.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Your membership inquiry has been received. We will respond privately.");
      setForm({ name: "", email: "", phone: "", heritage: "", research_status: "", enrollment_status: "", location: "", heard_about: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none py-3 mt-2 font-sans text-[15px] text-[#F5F0EB] placeholder:text-[#5f5c57] transition-colors";
  const selTrigger = "w-full bg-transparent border-0 border-b border-gold/30 rounded-none focus:ring-0 focus:border-gold text-[#F5F0EB] font-sans px-0 h-12";

  const SelField = ({ label, k, options, required }) => (
    <div>
      <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60 block mb-2">{label}{required ? " *" : ""}</label>
      <Select value={form[k]} onValueChange={setSel(k)}>
        <SelectTrigger data-testid={`inquiry-${k}`} className={selTrigger}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent className="bg-ink-surface border-gold/20 text-[#F5F0EB]">
          {options.map((c) => <SelectItem key={c} value={c} className="font-sans text-sm focus:bg-gold/10 focus:text-gold">{c}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div>
      <PageHeader
        eyebrow="Membership Inquiry"
        title="Begin your path to recognition with the Trust."
        subtitle="Share your lineage details below. Verified independent descendants of Woodland Mississippian and Modern Freedmen heritage are invited to inquire about membership. All information is held in strict confidence."
      />

      <section className="py-20 bg-ink">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} data-testid="contact-form" className="border border-gold/20 bg-ink-surface p-8 sm:p-10 space-y-7">
                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Full Name *</label>
                    <input data-testid="contact-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Email *</label>
                    <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Phone</label>
                    <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(optional)" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Location / Region</label>
                    <input data-testid="contact-location" value={form.location} onChange={set("location")} className={inputCls} placeholder="City, State / Region" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-7">
                  <SelField label="Heritage / Lineage" k="heritage" options={HERITAGE} required />
                  <SelField label="Genealogy Research" k="research_status" options={RESEARCH} required />
                </div>
                <SelField label="Tribal Enrollment Status" k="enrollment_status" options={ENROLLMENT} required />
                <div>
                  <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">How did you hear about the Trust?</label>
                  <input data-testid="contact-heard" value={form.heard_about} onChange={set("heard_about")} className={inputCls} placeholder="(optional)" />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Tell us about your lineage</label>
                  <textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={4} className={`${inputCls} resize-none`} placeholder="Share any details about your ancestry, documentation, or questions." />
                </div>
                <div className="border-t border-gold/15 pt-6">
                  <div className="flex items-start gap-3">
                    <ShieldCheck size={18} className="text-gold shrink-0 mt-0.5" strokeWidth={1.4} />
                    <p data-testid="inquiry-nda-clause" className="font-sans text-xs text-[#8f8b86] leading-relaxed">
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-gold/70 block mb-1">Non-Disclosure Agreement</span>
                      By submitting this inquiry, you acknowledge and agree that all information exchanged with
                      the 7 Generations Indigenous Trust — including your lineage details, documentation, and any
                      communications that follow — is <span className="text-[#A19D98]">strictly confidential</span> and
                      protected under a mutual Non-Disclosure Agreement. You further agree <span className="text-[#A19D98]">not to
                      disclose any information about the Trust</span> that may be received by you to any third party
                      without verifiable written or digital proof of authorization bearing the official seal of the
                      Trust. Clicking "Submit Membership Inquiry" constitutes your electronic signature and binding
                      acceptance of these NDA disclosure terms.
                    </p>
                  </div>
                </div>
                <button type="submit" disabled={busy} data-testid="contact-submit" className="flex items-center gap-3 px-8 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-gold-text transition-colors disabled:opacity-60">
                  {busy ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <><Send size={15} /> Submit Membership Inquiry</>}
                </button>
              </form>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="border border-gold/15 bg-ink-surface p-8">
                <BadgeCheck size={26} className="text-gold" strokeWidth={1.3} />
                <h4 className="font-serif text-2xl mt-5">What Happens Next</h4>
                <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4">
                  The Office of the Steward reviews each inquiry privately. Eligible descendants are invited to
                  anchor their genealogy through the Vault and receive their digital economic citizenship credential.
                </p>
                <Link to="/eligibility" data-testid="inquiry-eligibility-link" className="group inline-flex items-center gap-3 mt-6 font-mono text-[11px] tracking-[0.2em] uppercase text-gold border-b border-gold/40 pb-1 hover:border-gold transition-colors">
                  Review eligibility <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
              <div className="border border-gold/15 bg-ink-surface p-8 mt-8">
                <Eyebrow>Third-Party Verification</Eyebrow>
                <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4">
                  Institutions authenticating a credential issued via the Vault can verify its validity instantly.
                </p>
                <Link to="/credential-verification" data-testid="contact-verify-link" className="group inline-flex items-center gap-3 mt-4 font-mono text-[11px] tracking-[0.2em] uppercase text-gold border-b border-gold/40 pb-1 hover:border-gold transition-colors">
                  Verify a credential <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
